import { useForm } from '@inertiajs/react'
import Heading from '@arianna/design-system/Heading'
import Text from '@arianna/design-system/Text'
import Input from '@arianna/design-system/Input'
import Button from '@arianna/design-system/Button'

interface Props {
  errors: Record<string, string>
  flash: Record<string, string>
}

// Admin login — Inertia page (mirrors the student auth/Login), forced to the
// Notturno theme via a scoped data-theme wrapper so it matches the admin panel
// regardless of the visitor's stored theme.
export default function AdminLogin({ errors, flash }: Props) {
  const form = useForm({
    admin: { email: '', password: '', remember_me: false },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.post('/admin/auth/sign_in', { forceFormData: false })
  }

  const errorMessage = errors.base ?? form.errors['admin.email'] ?? form.errors['admin.password']

  return (
    <div
      data-theme="notturno"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
        fontFamily: 'var(--font-sans)',
        padding: 'var(--space-6)',
        color: 'var(--ink)',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 22,
          fontWeight: 600,
          color: 'var(--ink)',
          marginBottom: 'var(--space-1)',
        }}
      >
        Arianna
      </div>
      <Heading variant="eyebrow" style={{ marginBottom: 'var(--space-8)' }}>
        Admin
      </Heading>

      <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-8)',
        }}
      >
        <Heading variant="h3" style={{ marginBottom: 'var(--space-1)' }}>
          Accedi
        </Heading>
        <Text
          variant="body"
          style={{ color: 'var(--ink-3)', display: 'block', marginBottom: 'var(--space-6)' }}
        >
          Pannello di amministrazione.
        </Text>

        {flash.notice && (
          <div
            style={{
              background: 'color-mix(in oklab, var(--green) 12%, transparent)',
              border: '1px solid var(--green)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
            }}
          >
            <Text variant="meta" style={{ color: 'var(--green)', fontSize: 13 }}>
              {flash.notice}
            </Text>
          </div>
        )}

        {errorMessage && (
          <div
            style={{
              background: 'color-mix(in oklab, var(--red) 12%, transparent)',
              border: '1px solid var(--red)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
            }}
          >
            <Text variant="meta" style={{ color: 'var(--red)', fontSize: 13 }}>
              {errorMessage}
            </Text>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
        >
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            autoFocus
            value={form.data.admin.email}
            onChange={(e) => form.setData('admin', { ...form.data.admin, email: e.target.value })}
            required
          />
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            value={form.data.admin.password}
            onChange={(e) =>
              form.setData('admin', { ...form.data.admin, password: e.target.value })
            }
            required
          />

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 13,
              color: 'var(--ink-3)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={form.data.admin.remember_me}
              onChange={(e) =>
                form.setData('admin', { ...form.data.admin, remember_me: e.target.checked })
              }
            />
            Ricordami
          </label>

          <Button
            type="submit"
            variant="accent"
            size="md"
            disabled={form.processing}
            style={{ width: '100%', marginTop: 'var(--space-2)' }}
          >
            {form.processing ? 'Accesso in corso…' : 'Accedi'}
          </Button>
        </form>
      </div>
    </div>
  )
}
